import { Link } from "@inertiajs/react"
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  MinusCircle,
} from "lucide-react"
import type { LucideIcon } from "lucide-react"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import type { FacilitySlaRow } from "@/types"

// Per-facility SLA compliance — the refinery-by-refinery view Joe's team runs
// their morning call off.
//
// `facilities.sla_bad_order_limit` is a CAP ON HOW MANY bad orders the
// facility can hold on site before the operation starts to get impacted
// (Marathon's own definition). The server derives `level` and
// `utilization_pct` (on-site count ÷ cap). A facility with no limit set is
// not "failing" — it is unmeasured, and says so.
//
// STATUS COLORS ARE THE RESERVED STATUS PALETTE, fixed in both themes, and they
// NEVER carry meaning alone: every card pairs the color with an icon and a word.

const LEVELS: Record<
  FacilitySlaRow["level"],
  { color: string; icon: LucideIcon; label: string }
> = {
  good: { color: "#0ca30c", icon: CheckCircle2, label: "In SLA" },
  warning: { color: "#ec835a", icon: AlertTriangle, label: "At risk" },
  critical: { color: "#d03b3b", icon: AlertOctagon, label: "Over limit" },
  unmeasured: { color: "#8a8984", icon: MinusCircle, label: "No SLA set" },
}

function FacilityCard({ row }: { row: FacilitySlaRow }) {
  const routes = useOrgRoutes()
  const { color, icon: Icon, label } = LEVELS[row.level]
  // Meter: fill = how much of the cap is used. Capped at 100 so an over-limit
  // facility shows a full red bar rather than overflowing the track.
  const filled = Math.min(row.utilization_pct ?? 0, 100)

  return (
    <Link
      href={routes.facilityPath(row.facility_id)}
      className="hover:border-foreground/20 block rounded-lg border p-4 transition-colors"
      data-testid={`sla-card-${row.facility_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="font-medium">{row.facility_name}</span>
        <span
          className="inline-flex shrink-0 items-center gap-1 text-xs font-medium"
          style={{ color }}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          {label}
        </span>
      </div>

      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-2xl font-semibold">{row.open_bad_orders}</span>
        <span className="text-muted-foreground text-xs">
          on-site bad {row.open_bad_orders === 1 ? "order" : "orders"}
        </span>
      </div>

      <div
        className="mt-3 h-1.5 w-full overflow-hidden rounded-full"
        style={{ backgroundColor: `${color}29` }}
        role="img"
        aria-label={`${row.facility_name}: ${
          row.utilization_pct === null
            ? "no SLA configured"
            : `${row.open_bad_orders} of ${row.sla_bad_order_limit} allowed on site`
        }`}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${filled}%`, backgroundColor: color }}
        />
      </div>

      <p className="text-muted-foreground mt-2 text-xs">
        {row.sla_bad_order_limit === null
          ? "No on-site limit configured"
          : `${row.open_bad_orders} of ${row.sla_bad_order_limit} allowed on site`}
      </p>
    </Link>
  )
}

export function FacilitySlaBoard({ rows }: { rows: FacilitySlaRow[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>SLA by facility</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">No facilities yet.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map((row) => (
              <FacilityCard key={row.facility_id} row={row} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
